import { inject, Injectable } from '@angular/core';
import mixpanel from 'mixpanel-browser';
import { SessionService } from '../session/session.service';
import { NavigationEnd, Router } from '@angular/router';
import { debounceTime, filter } from 'rxjs/operators';
import { RolesEnum } from '../../enums/roles-enum';
import { StorageService } from '../storage/storage.service';

declare const MIX_PANEL_PROJECT_ID;
declare const MIX_PANEL_ENABLED: string;

const SPECIFIC_PATHS = [
  {
    path: '/program/new',
    pathName: 'Program Setup',
  },
  {
    path: '/surveys',
    pathName: 'Surveys Tab',
  },
  {
    path: '/reports/advanced/portfolio2_5',
    pathName: 'Advanced Reports',
  },
  {
    path: '/program',
    pathName: 'Manage Program',
  },
  {
    path: '/reports/portfolio/initiative',
    pathName: 'Portfolio Reports',
  },
];

@Injectable({
  providedIn: 'root',
})
export class MixpanelService {
  private session = inject(SessionService);
  private router = inject(Router);
  private storage = inject(StorageService);
  private isEnabled: boolean;

  constructor() {
    this.isEnabled =
      typeof MIX_PANEL_ENABLED !== 'undefined' && MIX_PANEL_ENABLED === 'true';

    if (this.isEnabled) {
      mixpanel.init(MIX_PANEL_PROJECT_ID, {
        autocapture: {
          pageview: 'full-url',
          click: true,
          input: true,
          scroll: true,
          submit: true,
          capture_text_content: false,
        },
      });

      mixpanel.set_config({
        persistence: 'localStorage',
        record_mask_text_selector: '',
        record_mask_text_class: new RegExp('^$'),
      });

      mixpanel.start_session_recording();
      this.trackPageView();
    }
  }

  private isMixpanelEnabled(): boolean {
    return this.isEnabled;
  }

  trackEventWithPathname(eventName: string, data: any) {
    if (!this.isMixpanelEnabled()) {
      return;
    }

    const { pathname } = window.location;
    const pageName = this.getPageNameFromPath(pathname);

    this.trackEvent(eventName, { ...data, pageName });
  }

  trackEvent(eventName: string, data: any) {
    if (!this.isMixpanelEnabled()) {
      return;
    }

    this.session.checkIfNeedNewSessionId();

    const loggedIn: boolean = this.session.loggedIn();
    const userId: number = this.session.getUser()?.id;
    const intermediary = this.session.getIntermediary();
    const networkID: number = intermediary?.id;
    const networkName: string = intermediary?.name;
    const programId: string = this.session.getProgram();
    const organization = this.session.getOrganization();
    const organizationId: number = organization ? organization.id : undefined;
    const organizationName: string = organization?.name;
    this.session.saveLastEventTime();
    const isNetworkAdministrator =
      this.storage.getRole() == RolesEnum.NETWORK_ADMINISTRATOR;

    const userProps = () => {
      const { role, email } = this.session.getAdminUser();
      const isEnterAs = role === RolesEnum.ADMINISTRATOR;
      const enterAsAdminEmail = isEnterAs ? email : null;
      return { isEnterAs, enterAsAdminEmail };
    };

    data = {
      ...data,
      userId,
      sessionId: this.session.getSessionId() || '',
      ...(loggedIn ? { ...userProps() } : {}),
      ...(programId ? { programId } : {}),
      ...(organizationId ? { organizationId } : {}),
      ...(organizationName ? { organizationName } : {}),
      ...(isNetworkAdministrator ? { networkID } : {}),
      ...(isNetworkAdministrator && networkName ? { networkName } : {}),
    };

    if (eventName === 'PS - Interactions') {
      delete data.programId;
    }
    mixpanel.track(eventName, data);
  }

  setIdentity(userId: number) {
    if (this.isMixpanelEnabled()) {
      mixpanel.identify(userId.toString());
    }
  }

  setUser(user: any) {
    if (this.isMixpanelEnabled()) {
      mixpanel.people.set(user);
    }
  }

  private trackPageView() {
    if (this.isMixpanelEnabled()) {
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          debounceTime(200)
        )
        .subscribe({
          next: () => {
            const { pathname, href } = window.location;
            const pageName = this.getPageNameFromPath(pathname);
            const isAccountAdministrator =
              this.storage.getRole() === RolesEnum.ACCOUNT_ADMINISTRATORS;
            const organizationId = this.session.getOrganization()
              ? this.session.getOrganization().id
              : undefined;

            this.trackEvent('Page View', {
              url: href,
              pathname: pathname,
              pageName: pageName,
              ...(isAccountAdministrator && organizationId
                ? { organizationId }
                : {}),
            });
          },
        });
    }
  }

  private convertToTitleCase(str: string): string {
    return str
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  private getPageNameFromPath(path: string): string {
    const match = SPECIFIC_PATHS.find((route) => path.includes(route.path));

    if (match) {
      return match.pathName;
    }

    const pathSegments = path.split('/').filter((segment) => segment !== '');

    if (pathSegments.length > 0) {
      return this.convertToTitleCase(pathSegments[0]);
    }
    return 'unknown';
  }
}
